import {
	AddIcon,
	Button,
	CopyIcon,
	DeleteIcon,
	Modal,
	ModuleHeader,
	PieChartIcon,
	SearchIcon,
	SpinnerWithBackground,
	Text,
} from '@viapeople/components'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../components/GenericTable'
import { useToast } from '../../utils/ToastContext'
import type { EnpsDashboardStore } from './stores/enpsDashboardStore'
import type { EnpsConfigListItem, SearchEnpsConfigsInput } from './types'

interface EnpsPageProps {
	store: () => EnpsDashboardStore
}

type SearchParams = Omit<SearchEnpsConfigsInput, 'organizationId'>

export const EnpsPage = ({ store }: EnpsPageProps) => {
	const {
		enpsConfigs,
		organization,
		selectedConfig,
		isLoading,
		error,
		searchQuery,
		initialize,
		search,
		duplicateSelectedConfig,
		deleteSelectedConfig,
		setSelectedConfig,
		setSearchQuery,
	} = store()

	const { organizationId } = useParams<{ organizationId: string }>()
	const navigate = useNavigate()
	const toast = useToast()
	const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
	const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
	const lastSearchValueRef = useRef<string | null>(null)

	useEffect(() => {
		if (organizationId) {
			// Reset search tracking when organization changes
			lastSearchValueRef.current = null
			initialize(organizationId)
		}
	}, [organizationId, initialize])

	useEffect(() => {
		if (error) {
			toast.showToast(error, 'error')
		}
	}, [error, toast])

	const handleOpenDuplicateModal = (config: EnpsConfigListItem) => {
		setSelectedConfig(config)
		setIsDuplicateModalOpen(true)
	}

	const handleOpenDeleteModal = (config: EnpsConfigListItem) => {
		setSelectedConfig(config)
		setIsDeleteModalOpen(true)
	}

	const handleDuplicate = async () => {
		try {
			await duplicateSelectedConfig()
			toast.showToast('NPS configuration duplicated successfully')
		} catch {
			// Error handling is done in the store
		} finally {
			setIsDuplicateModalOpen(false)
		}
	}

	const handleDelete = async () => {
		try {
			await deleteSelectedConfig()
			toast.showToast('NPS configuration deleted successfully')
		} catch {
			// Error handling is done in the store
		} finally {
			setIsDeleteModalOpen(false)
		}
	}

	const handleRowClick = (config: EnpsConfigListItem) => {
		navigate(`/organizations/${organizationId}/enps/${config.id}/general`)
	}

	// Handle external search changes with debouncing
	const handleSearchChange = useCallback(
		(options: Partial<SearchParams>) => {
			const value = options.search ?? ''

			// Skip if search value hasn't changed (prevents duplicate API calls on mount)
			if (lastSearchValueRef.current === value) {
				return
			}

			// Skip initial callback from GenericTable if we just initialized
			// (data is already fresh with empty search)
			if (lastSearchValueRef.current === null && value === '' && organization?.id === organizationId) {
				lastSearchValueRef.current = value
				return
			}

			lastSearchValueRef.current = value
			setSearchQuery(value)

			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}

			searchTimeoutRef.current = setTimeout(() => {
				if (organizationId) {
					search({ organizationId })
				}
			}, 300)
		},
		[organizationId, setSearchQuery, search, organization?.id],
	)

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}
		}
	}, [])

	// Create conditional empty state based on search context
	const getEmptyState = useMemo(() => {
		if (searchQuery.trim().length > 0) {
			return {
				icon: SearchIcon,
				title: 'No NPS surveys match your search',
				description: 'Try adjusting your search terms or clear the search to see all surveys',
			}
		}

		return {
			icon: PieChartIcon,
			title: 'No NPS surveys created yet',
			description: 'Get started by creating your first NPS survey',
			actionLabel: 'Create NPS Survey',
			onAction: () => navigate(`/organizations/${organizationId}/enps/create/general`),
		}
	}, [searchQuery, navigate, organizationId])

	if (isLoading) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	const formatStatus = (status: string) => {
		return status.charAt(0).toUpperCase() + status.slice(1)
	}

	const formatCadence = (cadence: string) => {
		return cadence.charAt(0).toUpperCase() + cadence.slice(1)
	}

	const columns: Column<EnpsConfigListItem>[] = [
		{
			field: 'name',
			label: 'Name',
			colSpan: 2,
			sortable: true,
			render: (config: EnpsConfigListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{config.name}
				</Text>
			),
		},
		{
			field: 'status',
			label: 'Status',
			colSpan: 1,
			sortable: true,
			render: (config: EnpsConfigListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{formatStatus(config.status)}
				</Text>
			),
		},
		{
			field: 'cadence',
			label: 'Cadence',
			colSpan: 1,
			sortable: true,
			render: (config: EnpsConfigListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{formatCadence(config.cadence)}
				</Text>
			),
		},
		{
			field: 'nextScheduledDate',
			label: 'Next Survey',
			colSpan: 1,
			sortable: true,
			render: (config: EnpsConfigListItem) => {
				const isValidDate = config.nextScheduledDate
				return (
					<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
						{isValidDate ? new Date(isValidDate).toLocaleDateString() : '-'}
					</Text>
				)
			},
		},
		{
			field: 'executionCount',
			label: 'Surveys Sent',
			colSpan: 1,
			sortable: false,
			render: (config: EnpsConfigListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{config.executionCount || 0}
				</Text>
			),
		},
		{
			field: 'responseCount',
			label: 'Responses',
			colSpan: 1,
			sortable: false,
			render: (config: EnpsConfigListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{config.responseCount || 0}
				</Text>
			),
		},
		{
			field: 'id' as keyof EnpsConfigListItem,
			label: 'Actions',
			colSpan: 1,
			sortable: false,
			render: (config: EnpsConfigListItem) => (
				// biome-ignore lint/a11y/noStaticElementInteractions: presentation container for event bubbling
				<div
					role='presentation'
					className='flex justify-start gap-2'
					onClick={(e) => e.stopPropagation()}
					onKeyDown={(e) => e.stopPropagation()}>
					<Button
						variant='secondary'
						size='small'
						onClick={(e) => {
							e.stopPropagation()
							handleOpenDuplicateModal(config)
						}}
						title='Duplicate survey'>
						<CopyIcon />
					</Button>
					<Button
						variant='destructive'
						size='small'
						onClick={(e) => {
							e.stopPropagation()
							handleOpenDeleteModal(config)
						}}
						title='Delete survey'>
						<DeleteIcon className='text-white' />
					</Button>
				</div>
			),
		},
	]

	return (
		<main className='p-6'>
			<ModuleHeader
				breadcrumb={[
					{ label: 'Organizations', to: '/organizations' },
					{ label: 'NPS', to: `/organizations/${organizationId}/enps` },
				]}
				title='NPS Surveys'
			/>

			<GenericTable<EnpsConfigListItem, SearchParams>
				data={{ data: enpsConfigs }}
				columns={columns}
				onRowClick={handleRowClick}
				searchValue={searchQuery}
				onSearchChange={handleSearchChange}
				actionButtons={[
					{
						children: 'Create NPS Survey',
						icon: AddIcon,
						onClick: () => navigate(`/organizations/${organizationId}/enps/create/general`),
						variant: 'primary',
					},
				]}
				emptyState={getEmptyState}
			/>

			{/* Duplicate Modal */}
			<Modal
				isOpen={isDuplicateModalOpen}
				onClose={() => setIsDuplicateModalOpen(false)}
				title='Duplicate NPS Survey'
				buttons={[
					{
						variant: 'secondary',
						onClick: () => setIsDuplicateModalOpen(false),
						children: 'Cancel',
					},
					{
						variant: 'primary',
						onClick: handleDuplicate,
						children: 'Duplicate',
					},
				]}>
				<Text variant='small'>
					Are you sure you want to duplicate <strong>"{selectedConfig?.name}"</strong>?
				</Text>
			</Modal>

			{/* Delete Modal */}
			<Modal
				isOpen={isDeleteModalOpen}
				onClose={() => setIsDeleteModalOpen(false)}
				title='Delete NPS Survey'
				buttons={[
					{
						variant: 'secondary',
						onClick: () => setIsDeleteModalOpen(false),
						children: 'Cancel',
					},
					{
						variant: 'destructive',
						onClick: handleDelete,
						children: 'Delete',
					},
				]}>
				<Text variant='small'>
					Are you sure you want to delete <strong>"{selectedConfig?.name}"</strong>? This action cannot be undone.
				</Text>
			</Modal>
		</main>
	)
}
